/**
 * 69.59, the helicopter heard (the sheet sound_sheets/aircraft_defence.html; the looks rendering/helicopterLooks.ts): the
 * rules, while `helicopterSound.ts` voices them — one looping voice a helicopter, the way the dropship's is (dropshipMix
 * `hearDropship`). Lane F is building its flight; nothing in play flies one yet.
 *   - **The rotor:** the blades' passing — a beat at the blade-pass rate (blades × the rotor's turns a second), a broad low
 *     thwop under it, and the slap (blade-vortex interaction): a sharp crack a blade, louder in a hard turn or a flare (the
 *     blades loaded, `load`) and at speed (the advancing tip). The rotor's beat carries furthest: half at `rotorReach`.
 *   - **The tail rotor:** a buzz at its own blade-pass rate.
 *   - **The turbines:** the whine (two engines, `twin`: two pitches a little apart), with the collective (`power`), heard
 *     only close.
 *   - **The wash on the ground:** under `washBelow` people the downwash tears at the ground — a roaring hiss and the grit
 *     thrown up, heard from the ground under it, stronger the lower it hovers. Unlike the gunship (30 up, past the
 *     dropship's 14), a helicopter at nap height reaches it.
 * In play (69.59 step 1's flight, GameConfig.helicopters): a flight of the `heli` craft is heard in `HELI_STYLE`
 * (dropshipSounds.ts), its state from its phase, speed, braking and height (`heliState`).
 * Two styles, by the looks (69.59's sheet): `two` the surplus utility's two-blade teeter rotor (look A: the deep, slapping
 * thwop), `four` the armed utility's four-blade rotor and twin turbines (look C: quicker, smoother, more drone). Real
 * figures for the rates: a two-blade rotor at ~324 turns a minute (10.8 Hz a blade), a four-blade at ~258 (17.2 Hz); their
 * tail rotors at ~1650 (2 blades, 55 Hz) and ~1190 (4, 79 Hz). Pure: no audio here. Unplayed guesses (decision 121).
 */
import { METRES_PER_PERSON, SPEED_OF_SOUND } from './gunfireMix';
import { HELI } from '../constants/helicopter';

export type HeliStyle = 'two' | 'four';
export const HELI_STYLES: readonly HeliStyle[] = ['two', 'four'];
/** The lean: the armed utility's (look C, 69.59's lean), with the two-blade kept for the surplus utility (look A). */
export const HELI_STYLE: HeliStyle = 'four';

export interface HeliVoiceNumbers {
  blades: number; rpm: number; tailBlades: number; tailRpm: number;
  /** the slap's share at rest (level flight, no load) and with full load */
  slapRest: number; slapLoaded: number;
  /** the thwop's level, the turbines' pitch (Hz), two engines */
  body: number; turbineHz: number; twin: boolean;
  gain: number; rotorReach: number; tailReach: number; turbineReach: number; range: number;
  /** the wash: heard under `washBelow` people of height, its loudness, half at `washReach` people from the ground under it */
  washBelow: number; wash: number; washReach: number;
}
export const HELI_VOICES: Record<HeliStyle, HeliVoiceNumbers> = {
  two: { blades: 2, rpm: 324, tailBlades: 2, tailRpm: 1650, slapRest: 0.45, slapLoaded: 1, body: 1, turbineHz: 4200, twin: false,
    gain: 0.9, rotorReach: 45, tailReach: 20, turbineReach: 12, range: 3000, washBelow: 20, wash: 1.2, washReach: 15 },
  four: { blades: 4, rpm: 258, tailBlades: 4, tailRpm: 1190, slapRest: 0.25, slapLoaded: 0.75, body: 0.8, turbineHz: 5200, twin: true,
    gain: 0.9, rotorReach: 42, tailReach: 20, turbineReach: 12, range: 2800, washBelow: 20, wash: 1.2, washReach: 15 },
};

/**
 * Its state as the sound reads it: `power` the collective (0.6 in the cruise, 0.8 in the hover, 1 climbing hard), `load` how
 * hard the blades are worked (0 level, ~0.7 in a banked turn, 1 in a hard flare), `speed` (people a second), its height over
 * the ground (people) and its closing speed toward the listener (people a second).
 */
export interface HeliState { power: number; load: number; speed: number; heightPeople: number; closing: number }

export interface HeliHeard {
  rotor: { gain: number; rate: number; slap: number; body: number };
  tail: { gain: number; hz: number };
  turbine: { gain: number; hz: number; spread: number };
  wash: { gain: number };
  cutoff: number; delay: number; pan: number;
}
const clamp = (x: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, x));

/** The collective on the ground (the rotor turning at flat pitch), the spool to full before a lift (s), the braking that loads the blades fully (people a second²). */
export const HELI_PLAY = { groundPower: 0.45, liftLead: 1.5, fullLoadBrake: 6 };

/**
 * A flight's state as the voice reads it: `phase` its flight's ('down' on the ground), `speed` people a second, `brake` its
 * deceleration (people a second², + slowing: the flare before a landing loads the blades), its height over the ground and
 * its closing speed (people). In the air the collective rises as it slows (a hover takes more than the cruise); on the ground
 * it idles, spooling to full over HELI_PLAY.liftLead before `liftTick`.
 */
export function heliState(phase: 'in' | 'down' | 'loiter' | 'out', tick: number, liftTick: number, tickSeconds: number, speed: number, brake: number, heightPeople: number, closing: number): HeliState {
  if (phase === 'down') {
    const lift = clamp(1 - (liftTick - tick) * tickSeconds / HELI_PLAY.liftLead, 0, 1);
    return { power: HELI_PLAY.groundPower + (1 - HELI_PLAY.groundPower) * lift, load: 0, speed: 0, heightPeople: 0, closing };
  }
  const slow = clamp(1 - Math.abs(speed) / HELI.speedPeople, 0, 1);
  return { power: 0.6 + 0.2 * slow, load: clamp(brake / HELI_PLAY.fullLoadBrake, 0, 1), speed: Math.abs(speed), heightPeople: Math.max(0, heightPeople), closing };
}

/** The slap's share: its rest, up with the load and at speed (past 25 people a second, full at 40). */
export function heliSlap(V: HeliVoiceNumbers, load: number, speed: number): number {
  const fast = clamp((speed - 25) / 15, 0, 1);
  return clamp(V.slapRest + (V.slapLoaded - V.slapRest) * clamp(load, 0, 1) + 0.2 * fast, 0, 1);
}

/**
 * The helicopter `people` away on its `pan` side; `washPeople` the listener's distance from the ground under it (its wash's
 * place; default `people`). Null past its range.
 */
export function hearHelicopter(style: HeliStyle, people: number, pan: number, s: HeliState, washPeople = people): HeliHeard | null {
  const V = HELI_VOICES[style], d = Math.max(0, people);
  if (d > V.range) return null;
  const doppler = clamp(1 + s.closing / 340, 0.8, 1.25), power = clamp(s.power, 0, 1);
  const low = clamp(1 - s.heightPeople / V.washBelow, 0, 1);
  return {
    rotor: { gain: V.gain * (0.55 + 0.45 * power) / (1 + d / V.rotorReach), rate: (V.blades * V.rpm / 60) * doppler, slap: heliSlap(V, s.load, s.speed), body: V.body },
    tail: { gain: V.gain * 0.3 / (1 + d / V.tailReach), hz: (V.tailBlades * V.tailRpm / 60) * doppler },
    turbine: { gain: V.gain * 0.12 * (0.5 + 0.5 * power) / (1 + d / V.turbineReach), hz: V.turbineHz * (0.97 + 0.03 * power) * doppler, spread: V.twin ? 0.012 : 0 },
    wash: { gain: low > 0 ? V.gain * V.wash * low * Math.sqrt(low) * power / (1 + Math.max(0, washPeople) / V.washReach) : 0 },
    cutoff: clamp(9000 * Math.exp(-d / 240), 200, 9000),   // (the beat carries: the slap's crack goes first)
    delay: (d * METRES_PER_PERSON) / SPEED_OF_SOUND,
    pan: clamp(pan, -1, 1),
  };
}
